import React from 'react';
import { connect } from 'react-redux';

import Enrollment from '../../../components/individualPortal/dashboard/Enrollment';
import { getActiveEnrollments } from '../../../utils/dashboard/processCommon';
import { cloneAndRedirectLCE } from '../../../actions/individualPortal/dashboard/dashboardActionCreators';
import { showModal } from '../../../actions/common/actionCreators';
import { dashboardModalTypes } from '../../../constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { ctaHandlers } from '../../../utils/dashboard/stateSpecificUtils';
import { processCommon } from '../../../utils/dashboard/processCommon';
import {
    PERIOD_TYPES,
    APP_STATUSES,
    APP_TYPES,
    ENROLLMENT_MESSAGES,
    ENROLLMENT_STATUSES,
    ENROLLMENT_TYPES,
} from '../../../constants/individualPortal/dashboard/dashboardModelTypes';
import { useHistory } from 'react-router-dom';
import { useStateCode } from '../../../hooks';

const EnrollmentContainer = props => {
    const {
        environment: {
            stateCode,
            user,
            links: { CALHEERS_URL },
            ssapFlowVersion,
            tobaccoEnabled,
            hardshipEnabled,
            localDates: { serverDate },
            flags: { userSystemErrorEnabled, preDocVerificationShoppingEnabled },
        },
        dashboardModels,
        activeTab,
        enrollmentType,
        cloneAndRedirectLCE,
        showModal,
    } = props;

    const history = useHistory();
    const { shouldRedirectToCustomGrouping } = useStateCode();
    const householdCaseId = user && user.householdCaseId;
    const activeTabData = dashboardModels[activeTab];
    const {
        applicationType,
        applicationStatus,
        isSepOpen,
        encryptedCaseNumber,
        enrollments,
        applicationDentalStatus,
        dashboardPreDocVerification,
    } = activeTabData;

    const { goToContinueApp, goToGroupingScreen, goToViewDetails } = ctaHandlers;
    const { isEnrolledInHealth } = processCommon;

    const getPeriod = isInsideOEWindow => (isInsideOEWindow ? PERIOD_TYPES.OE : PERIOD_TYPES.OUTSIDE_OE);

    const showChangePlansLink = () => {
        // This feature will be driven by configuration "UserSystemError" - <userSystemErrorEnabled>.
        if (!userSystemErrorEnabled || applicationType === APP_TYPES.OE || isSepOpen) return false;

        const activeEnrollments = getActiveEnrollments(enrollments);
        if (activeEnrollments && activeEnrollments.length === 0) return false;

        const isActiveHealthEnrollments = isEnrolledInHealth(activeEnrollments);

        // When app status is ER and applicationDentalStatus is EN then we show the link in “Your Dental Plans” section.
        if (enrollmentType === ENROLLMENT_TYPES.DENTAL) {
            if (isActiveHealthEnrollments) return false;
            if (applicationStatus === APP_STATUSES.ER && applicationDentalStatus === APP_STATUSES.EN) {
                return true;
            }
        }

        if (enrollmentType === ENROLLMENT_TYPES.HEALTH) {
            if (!isActiveHealthEnrollments) return false;
            if ([APP_STATUSES.EN, APP_STATUSES.PN].includes(applicationStatus)) return true;
        }
    };

    const filterEnrollees = (enrollmentStatus, enrollees) => {
        // If enrollee has this status, we are not going to display it.
        const ENROLLEE_STATUS = ['CANCEL', 'TERM'];
        const [ENROLLEE_STATUS_CANCEL] = ENROLLEE_STATUS;
        const ENROLLMENT_STATUS_TERM = 'TERM';

        // If enrollmentStatus is 'TERM' any enrollee with CANCEL status will not be shown,
        // any enrollee with any status other than CANCEL ( ACTIVE, PENDING, TERM) will be shown.
        if (enrollmentStatus === ENROLLMENT_STATUS_TERM) {
            return enrollees.filter(enrollee => enrollee.status !== ENROLLEE_STATUS_CANCEL);
        } else {
            return enrollees.filter(enrollee => !ENROLLEE_STATUS.includes(enrollee.status));
        }
    };

    const getFavPlansByType = (planType, preEligibility) => {
        if (preEligibility && preEligibility.plans && preEligibility.plans.length > 0) {
            const favPlansByType = preEligibility.plans.filter(x => x.type.toLowerCase() === planType.toLowerCase());
            return favPlansByType.length > 0 ? favPlansByType : false;
        } else {
            return false;
        }
    };

    const getEnrollmentStatus = (status, coverageEndDate) => {
        if (status === ENROLLMENT_STATUSES.TERMINATED && serverDate.isBefore(coverageEndDate)) {
            return ENROLLMENT_STATUSES.FUTURE_TERM;
        }
        return status;
    };

    const getEnrollmentsByType = (planType, enrollments) => {
        if (!enrollments || enrollments.length <= 0) return false;

        let planTypeEnrollments = enrollments.filter(x => x.type.toLowerCase() === planType.toLowerCase());

        return planTypeEnrollments.length > 0
            ? planTypeEnrollments.map(enrollment => ({
                id: enrollment.id,
                plan: enrollment.plan,
                enrollees:
                    (enrollment.enrollees && filterEnrollees(enrollment.enrollmentStatus, enrollment.enrollees)) ||
                    null,
                status: getEnrollmentStatus(enrollment.enrollmentStatus, enrollment.coverageEndDate),
                coverageEndDate: enrollment.coverageEndDate,
            }))
            : false;
    };

    const getSavedPlansByType = (planType, savedPlans) => {
        if (savedPlans && savedPlans.length > 0) {
            const savedPlansByType = savedPlans.filter(plan => plan.type.toLowerCase() === planType.toLowerCase());
            return savedPlansByType.length > 0 ? savedPlansByType : false;
        } else {
            return false;
        }
    };

    // We render either enrollment data, favorite plans or saved plans(preDocVerification data).
    const getEnrollmentData = (type, data) => {
        const {
            enrollments,
            preEligibility,
            applicationStatus,
            isInsideOEWindow,
            dashboardPreDocVerification,
            sepEvent,
        } = data;
        const savedPlans = dashboardPreDocVerification && dashboardPreDocVerification.plans;
        const enrollmentsByType = getEnrollmentsByType(type, enrollments);
        const favoritePlansByType = getFavPlansByType(type, preEligibility);
        const savedPlansByType = getSavedPlansByType(type, savedPlans);
        const period = getPeriod(isInsideOEWindow);

        const preDocVerificationCondition =
            ['SHOP_WHILE_YOU_WAIT', 'PENDED_PLAN_SELECTION'].includes(preDocVerificationShoppingEnabled) &&
            !enrollmentsByType &&
            (!favoritePlansByType || (applicationStatus === APP_STATUSES.ER && period === PERIOD_TYPES.OUTSIDE_OE)) &&
            isSepOpen &&
            sepEvent &&
            !sepEvent.isDocumentUploadedAndVerified;

        // id 0a, 0b, 4a, 4b
        if (preDocVerificationCondition) {
            let preDocVerificationRes = {
                preDocVerificationSavedPlans: [],
                status: null,
                link: type === ENROLLMENT_TYPES.HEALTH ? ENROLLMENT_MESSAGES.PICK_PLAN : null,
            };
            if (savedPlansByType) {
                preDocVerificationRes.preDocVerificationSavedPlans = savedPlansByType;
                preDocVerificationRes.status = ENROLLMENT_STATUSES.SAVED_PLAN;
                preDocVerificationRes.link =
                    type === ENROLLMENT_TYPES.HEALTH ? ENROLLMENT_MESSAGES.PICK_DIFF_PLAN : null;
            }
            return preDocVerificationRes;
        }

        // id 5,6,7,8 when there are enrollments
        if (enrollmentsByType) {
            let enrollmentsRes = {
                preDocVerificationShoppingEnabled,
                enrollments: enrollmentsByType,
                link: ENROLLMENT_MESSAGES.VIEW_DETAILS,
            };
            enrollmentsRes.enrollments = enrollmentsByType;
            return enrollmentsRes;

            // id 1,2,3,4 no enrollments, but favorite plans are present
        } else if (favoritePlansByType) {
            let favoritePlansRes = {
                favPlans: favoritePlansByType,
                status: ENROLLMENT_MESSAGES.IN_YOUR_CART,
            };

            switch (applicationStatus) {
                case APP_STATUSES.ER:
                case APP_STATUSES.PN:
                case APP_STATUSES.EN:
                    favoritePlansRes.link = period === PERIOD_TYPES.OE ? ENROLLMENT_MESSAGES.CONTINUE_SHOPPING : null;
                    break;
                case APP_STATUSES.OP:
                default:
                    favoritePlansRes.link = ENROLLMENT_MESSAGES.COMPLETE_APP_TO_ENROLL;
                    break;
            }
            return favoritePlansRes;
        } else {
            // id 0 Default case, when there are no enrollments, no favorite plans, no preDocVerification plans.
            return false;
        }
    };

    const enrollmentCTA = (stateCode, tabData, link) => {
        const {
            caseNumber,
            applicationStatus,
            applicationYear,
            isInsideOEWindow,
            isFinancial,
            ssapApplicationId,
            dashboardPreDocVerification,
            applicants,
        } = tabData;
        const isTobaccoInformationAvailableForAllUser = !applicants?.some(
            applicant => [null, undefined, 'undefined'].includes(applicant.tobacco),
        );
        const ssapParams = {
            ssapFlowVersion,
            caseNumber,
            applicationStatus,
            applicationYear,
            isInsideOEWindow,
            isFinancial,
            ssapApplicationId,
        };
        const futureCoverageStartDate =
            dashboardPreDocVerification && dashboardPreDocVerification.futureCoverageStartDate;

        switch (link) {
            case ENROLLMENT_MESSAGES.COMPLETE_APP_TO_ENROLL:
                goToContinueApp(stateCode, ssapParams, CALHEERS_URL, householdCaseId);
                break;

            case ENROLLMENT_MESSAGES.CONTINUE_SHOPPING:
            case ENROLLMENT_MESSAGES.PICK_PLAN:
                goToGroupingScreen({
                    tobaccoEnabled,
                    hardshipEnabled,
                    encryptedCaseNumber,
                    history,
                    applicationStatus,
                    isTobaccoInformationAvailableForAllUser,
                    shouldRedirectToCustomGrouping,
                });
                break;

            case ENROLLMENT_MESSAGES.VIEW_DETAILS:
                goToViewDetails();
                break;
            case ENROLLMENT_MESSAGES.PICK_DIFF_PLAN:
                showModal({
                    modalType: dashboardModalTypes.PICK_DIFF_PLAN,
                    modalData: {
                        futureCoverageStartDate,
                        cb: goToGroupingScreen.bind(null, {
                            tobaccoEnabled,
                            hardshipEnabled,
                            encryptedCaseNumber,
                            history,
                            applicationStatus,
                            isTobaccoInformationAvailableForAllUser,
                            shouldRedirectToCustomGrouping,
                        }),
                    },
                });
                break;

            default:
                break;
        }
    };

    const enrollmentData = getEnrollmentData(enrollmentType, activeTabData);

    return (
        <Enrollment
            type={enrollmentType}
            data={enrollmentData}
            effectiveStartDate={dashboardPreDocVerification && dashboardPreDocVerification.effectiveStartDate}
            onCTAHandler={() => enrollmentCTA(stateCode, activeTabData, enrollmentData.link)}
            showChangePlansLink={showChangePlansLink()}
            exceptionalCircumstanceCb={() => cloneAndRedirectLCE(encryptedCaseNumber)}
        />
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    dashboardModels: state.individualPortal.dashboard.dashboardModels,
    activeTab: state.individualPortal.dashboard.activeTab,
});

export default connect(mapStateToProps, { cloneAndRedirectLCE, showModal })(EnrollmentContainer);
